import { type FormEvent, useMemo, useState } from 'react';

import { formatarDataHora } from '@/nucleo/formato/datas';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Checkbox } from '@/ui/base/checkbox';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

import {
  type CicloDetalhe,
  doCampoLocal,
  type Dre,
  type EntradaDoCiclo,
  MODALIDADES,
  type Modalidade,
  paraCampoLocal,
  ROTULO_DA_MODALIDADE,
} from './contrato';

const chave = (dre: string, modalidade: Modalidade) => `${dre}|${modalidade}`;

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  dres: Dre[];
  /** Sem ciclo, cria; com ciclo, altera. */
  ciclo?: CicloDetalhe;
  enviando: boolean;
  erro: string | null;
  aoEnviar: (entrada: EntradaDoCiclo, motivo: string | undefined) => void;
}

/**
 * Formulário do ciclo (RN-CP-01, RN-CP-02): dados do período e a grade de vagas
 * DRE × modalidade. A grade se monta sozinha a partir das DREs e modalidades
 * marcadas — é sempre o produto completo, e cada combinação pede um número.
 */
export function FormularioDoCiclo({
  aberto,
  aoFechar,
  dres,
  ciclo,
  enviando,
  erro,
  aoEnviar,
}: Props) {
  const c = ciclo?.ciclo;
  const publicado = c?.status === 'ABERTO';
  const inicioJaPassou = publicado && !!c?.inicioEm && new Date(c.inicioEm).getTime() <= Date.now();

  const [nome, setNome] = useState(c?.nome ?? '');
  const [descricao, setDescricao] = useState(c?.descricao ?? '');
  const [inicio, setInicio] = useState(paraCampoLocal(c?.inicioEm));
  const [fim, setFim] = useState(paraCampoLocal(c?.fimEm));
  const [editalVersao, setEditalVersao] = useState(c?.editalVersao ?? '');
  const [maxEscolas, setMaxEscolas] = useState(String(c?.maxEscolas ?? 3));
  const [prazo, setPrazo] = useState(c?.prazoRespostaDias ? String(c.prazoRespostaDias) : '');
  const [motivo, setMotivo] = useState('');
  const [dresMarcadas, setDresMarcadas] = useState<string[]>(() => [
    ...new Set(ciclo?.vagas.map((v) => v.dreId) ?? []),
  ]);
  const [modalidadesMarcadas, setModalidadesMarcadas] = useState<Modalidade[]>(() =>
    MODALIDADES.filter((m) => ciclo?.vagas.some((v) => v.modalidade === m)),
  );
  const [vagas, setVagas] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (ciclo?.vagas ?? []).map((v) => [chave(v.dreId, v.modalidade), String(v.quantidade)]),
    ),
  );
  const [aviso, setAviso] = useState<string | null>(null);

  const dresDaGrade = useMemo(
    () => dres.filter((d) => dresMarcadas.includes(d.id)),
    [dres, dresMarcadas],
  );

  function alternar<T>(lista: T[], item: T): T[] {
    return lista.includes(item) ? lista.filter((x) => x !== item) : [...lista, item];
  }

  function validar(): string | null {
    if (nome.trim().length < 3) return 'Informe o nome do ciclo, com ao menos 3 caracteres.';
    if (!inicio || !fim) return 'Informe a data e a hora de início e de fim do período.';
    if (new Date(fim).getTime() <= new Date(inicio).getTime())
      return 'O fim do período precisa ser depois do início.';
    if (!editalVersao.trim()) return 'Informe a versão do edital.';
    const p = Number(prazo);
    if (!Number.isInteger(p) || p < 1 || p > 60)
      return 'Informe o prazo de resposta do pré-credenciado, de 1 a 60 dias.';
    if (dresMarcadas.length > 0 !== modalidadesMarcadas.length > 0)
      return 'Escolha ao menos uma DRE e uma modalidade, ou deixe as duas listas vazias no rascunho.';
    if (publicado && dresMarcadas.length === 0)
      return 'Ciclo aberto precisa de ao menos uma DRE e uma modalidade.';
    for (const d of dresDaGrade) {
      for (const m of modalidadesMarcadas) {
        const q = vagas[chave(d.id, m)];
        if (q === undefined || q === '' || !Number.isInteger(Number(q)) || Number(q) < 0)
          return `Informe as vagas de ${d.nome} em ${ROTULO_DA_MODALIDADE[m]} (número inteiro, zero ou mais).`;
      }
    }
    if (publicado && motivo.trim().length < 10)
      return 'Ciclo aberto só muda com o motivo registrado. Escreva o motivo, com ao menos 10 caracteres.';
    return null;
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const problema = validar();
    setAviso(problema);
    if (problema) return;
    aoEnviar(
      {
        nome: nome.trim(),
        descricao: descricao.trim() || undefined,
        inicioEm: inicioJaPassou && c?.inicioEm ? c.inicioEm : doCampoLocal(inicio),
        fimEm: doCampoLocal(fim),
        editalVersao: editalVersao.trim(),
        maxEscolas: Number(maxEscolas),
        prazoRespostaDias: Number(prazo),
        vagas: dresDaGrade.flatMap((d) =>
          modalidadesMarcadas.map((m) => ({
            dreId: d.id,
            modalidade: m,
            quantidade: Number(vagas[chave(d.id, m)]),
          })),
        ),
      },
      publicado ? motivo.trim() : undefined,
    );
  }

  const mensagem = aviso ?? erro;

  return (
    <ResponsiveModal
      open={aberto}
      onOpenChange={(v) => !v && aoFechar()}
      title={ciclo ? 'Alterar ciclo de inscrição' : 'Novo ciclo de inscrição'}
      description={
        publicado
          ? 'O ciclo está aberto: a alteração fica registrada na trilha, com o motivo.'
          : 'O ciclo nasce como rascunho. Ele só abre com o edital enviado e a grade de vagas completa.'
      }
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
            Voltar
          </Button>
          <Button type="submit" form="formulario-ciclo" disabled={enviando}>
            {enviando ? 'Salvando…' : ciclo ? 'Salvar alteração' : 'Criar rascunho'}
          </Button>
        </div>
      }
    >
      <form id="formulario-ciclo" noValidate onSubmit={enviar} className="space-y-6">
        {mensagem && (
          <Alert variant="destructive">
            <AlertDescription>{mensagem}</AlertDescription>
          </Alert>
        )}

        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold">Período e edital</legend>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ciclo-nome">Nome</Label>
            <Input
              id="ciclo-nome"
              value={nome}
              maxLength={120}
              onChange={(e) => setNome(e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ciclo-descricao">Descrição (opcional)</Label>
            <Textarea
              id="ciclo-descricao"
              value={descricao}
              maxLength={1000}
              rows={2}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </div>
          {inicioJaPassou ? (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Início das inscrições
              </p>
              <p className="text-sm">
                {formatarDataHora(c?.inicioEm)} — o período já começou, e o início não muda.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="ciclo-inicio">Início das inscrições</Label>
              <Input
                id="ciclo-inicio"
                type="datetime-local"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="ciclo-fim">Fim das inscrições</Label>
            <Input
              id="ciclo-fim"
              type="datetime-local"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ciclo-edital">Versão do edital</Label>
            <Input
              id="ciclo-edital"
              value={editalVersao}
              maxLength={40}
              placeholder="Ex.: Edital 01/2027"
              onChange={(e) => setEditalVersao(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ciclo-escolas">Teto de escolas por veículo</Label>
            <select
              id="ciclo-escolas"
              className="flex h-11 w-full rounded-xl border border-input bg-background px-3.5 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={maxEscolas}
              onChange={(e) => setMaxEscolas(e.target.value)}
            >
              <option value="1">1 escola</option>
              <option value="2">Até 2 escolas</option>
              <option value="3">Até 3 escolas</option>
            </select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ciclo-prazo">Prazo de resposta do pré-credenciado (dias)</Label>
            <Input
              id="ciclo-prazo"
              type="number"
              inputMode="numeric"
              min={1}
              max={60}
              value={prazo}
              onChange={(e) => setPrazo(e.target.value)}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-2 text-sm font-semibold">Modalidades aceitas</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {MODALIDADES.map((m) => (
              <label
                key={m}
                className="flex min-h-[44px] items-center gap-3 rounded-lg border border-border px-3 text-sm"
              >
                <Checkbox
                  checked={modalidadesMarcadas.includes(m)}
                  onCheckedChange={() =>
                    setModalidadesMarcadas((l) =>
                      MODALIDADES.filter((x) => alternar(l, m).includes(x)),
                    )
                  }
                />
                {ROTULO_DA_MODALIDADE[m]}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="mb-2 text-sm font-semibold">DREs aceitas</legend>
          {dres.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhuma diretoria está cadastrada ainda. A lista vem do cadastro escolar (EOL); sem
              ela, o rascunho pode ser salvo, mas o ciclo não abre.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDresMarcadas(dres.map((d) => d.id))}
                >
                  Marcar todas
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDresMarcadas([])}
                >
                  Desmarcar todas
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {dres.map((d) => (
                  <label
                    key={d.id}
                    className="flex min-h-[44px] items-center gap-3 rounded-lg border border-border px-3 text-sm"
                  >
                    <Checkbox
                      checked={dresMarcadas.includes(d.id)}
                      onCheckedChange={() => setDresMarcadas((l) => alternar(l, d.id))}
                    />
                    <span className="min-w-0 wrap-break-word">{d.nome}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </fieldset>

        {dresDaGrade.length > 0 && modalidadesMarcadas.length > 0 && (
          <fieldset className="space-y-3">
            <legend className="mb-1 text-sm font-semibold">Vagas por DRE e modalidade</legend>
            <p className="text-xs text-muted-foreground">
              Informe todas as combinações. Zero é permitido: quem se inscrever nela entra na lista
              de espera do sorteio.
            </p>
            {dresDaGrade.map((d) => (
              <div key={d.id} className="rounded-xl border border-border p-3">
                <p className="mb-2 text-sm font-medium">{d.nome}</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {modalidadesMarcadas.map((m) => {
                    const id = `vaga-${d.id}-${m}`;
                    return (
                      <div key={m} className="space-y-1">
                        <Label htmlFor={id} className="normal-case tracking-normal">
                          {ROTULO_DA_MODALIDADE[m]}
                          <span className="sr-only"> — vagas em {d.nome}</span>
                        </Label>
                        <Input
                          id={id}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          value={vagas[chave(d.id, m)] ?? ''}
                          onChange={(e) =>
                            setVagas((v) => ({ ...v, [chave(d.id, m)]: e.target.value }))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </fieldset>
        )}

        {publicado && (
          <div className="space-y-1.5">
            <Label htmlFor="ciclo-motivo">Motivo da alteração</Label>
            <Textarea
              id="ciclo-motivo"
              value={motivo}
              maxLength={1000}
              rows={3}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
        )}
      </form>
    </ResponsiveModal>
  );
}
